/**
 * Base64 Encoder & Decoder — 100% Client-Side Suite
 * Replicating and improvising Authgear Base64 Encode/Decode tool:
 * - Real-time live bi-directional encoding & decoding
 * - Standard Base64 (RFC 4648) & Base64URL (RFC 4648 §5 for JWTs)
 * - Multi-character set support: UTF-8, ASCII, UTF-16LE, UTF-16BE
 * - Custom padding & line wrapping controls (PEM 64-col, MIME 76-col)
 * - Interactive Hex byte sequence analyzer
 * - Zero file uploads: pure, privacy-first text & token processing
 */

(function () {
  'use strict';

  // Sample Presets
  const PRESETS = {
    hello: "Hello, World! Welcome to JustOnlyOneThing Base64 Suite.",
    jwt: JSON.stringify({ alg: "HS256", typ: "JWT" }, null, 2) + "\n" + JSON.stringify({ sub: "usr_94812", name: "Alice Henderson", role: "SuperAdmin", iat: 1716239022 }, null, 2),
    basicAuth: "admin:SuperSecretPassword!2026",
    unicode: "Bonjour le monde! 🌍 Привет мир 🚀 こんにちは世界 ✨ مرحبا بالعالم",
    json: JSON.stringify({ app: "payment-gateway", version: "3.2.0", ssl: true, timeoutMs: 5000, allowedOrigins: ["https://justonlyonething.com"] }, null, 2)
  };

  // Toast notification helper
  function showToast(msg, icon = '✓') {
    const existing = document.querySelector('.b64-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'b64-toast je-toast';
    toast.innerHTML = `<span>${icon}</span> <span>${msg}</span>`;
    document.body.appendChild(toast);
    setTimeout(() => {
      toast.style.opacity = '0';
      toast.style.transition = 'opacity 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // Safe chunked binary to base64 conversion
  function bytesToBase64(bytes) {
    let binary = '';
    const len = bytes.byteLength;
    const chunkSize = 8192;
    for (let i = 0; i < len; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, len));
      binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
  }

  // Safe chunked base64 to bytes conversion
  function base64ToBytes(base64Str) {
    const binary = atob(base64Str);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  // Multi-charset string to byte conversion
  function stringToBytes(str, charset) {
    if (charset === 'ascii') {
      const bytes = new Uint8Array(str.length);
      for (let i = 0; i < str.length; i++) {
        bytes[i] = str.charCodeAt(i) & 0xff;
      }
      return bytes;
    } else if (charset === 'utf-16le') {
      const buf = new ArrayBuffer(str.length * 2);
      const view = new DataView(buf);
      for (let i = 0; i < str.length; i++) {
        view.setUint16(i * 2, str.charCodeAt(i), true);
      }
      return new Uint8Array(buf);
    } else if (charset === 'utf-16be') {
      const buf = new ArrayBuffer(str.length * 2);
      const view = new DataView(buf);
      for (let i = 0; i < str.length; i++) {
        view.setUint16(i * 2, str.charCodeAt(i), false);
      }
      return new Uint8Array(buf);
    }
    // Default: UTF-8
    return new TextEncoder().encode(str);
  }

  // Multi-charset bytes to string conversion
  function bytesToString(bytes, charset) {
    if (charset === 'ascii') {
      let str = '';
      for (let i = 0; i < bytes.length; i++) {
        str += String.fromCharCode(bytes[i]);
      }
      return str;
    } else if (charset === 'utf-16le') {
      return new TextDecoder('utf-16le').decode(bytes);
    } else if (charset === 'utf-16be') {
      return new TextDecoder('utf-16be').decode(bytes);
    }
    // Default: UTF-8
    return new TextDecoder('utf-8', { fatal: false }).decode(bytes);
  }

  // Core App Controller
  document.addEventListener('DOMContentLoaded', () => {
    let mode = 'encode'; // 'encode' | 'decode'
    let flavor = 'standard'; // 'standard' | 'url'
    let charset = 'utf-8'; // 'utf-8' | 'ascii' | 'utf-16le' | 'utf-16be'
    let padding = true; // true | false
    let lineWrap = 'none'; // 'none' | '64' | '76'

    // DOM Elements
    const inputArea = document.getElementById('b64Input');
    const outputArea = document.getElementById('b64Output');
    const inputTitle = document.getElementById('b64InputTitle');
    const outputTitle = document.getElementById('b64OutputTitle');
    const inputMeta = document.getElementById('b64InputMeta');
    const outputMeta = document.getElementById('b64OutputMeta');
    const inputStatus = document.getElementById('b64InputStatus');
    const outputStatus = document.getElementById('b64OutputStatus');
    const hexContainer = document.getElementById('b64HexGrid');
    const hexDrawer = document.getElementById('b64HexDrawer');

    // Controls
    const pillEncode = document.getElementById('pillEncode');
    const pillDecode = document.getElementById('pillDecode');
    const pillStandard = document.getElementById('pillStandard');
    const pillUrl = document.getElementById('pillUrl');
    const presetSelect = document.getElementById('b64PresetSelect');
    const charsetSelect = document.getElementById('b64CharsetSelect');
    const paddingCheck = document.getElementById('b64PaddingCheck');
    const lineWrapSelect = document.getElementById('b64LineWrapSelect');
    const btnSwap = document.getElementById('b64BtnSwap');
    const btnCopy = document.getElementById('b64BtnCopy');
    const btnClear = document.getElementById('b64BtnClear');
    const btnDownload = document.getElementById('b64BtnDownload');
    const btnToggleHex = document.getElementById('b64BtnToggleHex');

    // Process conversion
    function process() {
      const input = inputArea.value;

      if (!input) {
        outputArea.value = '';
        inputMeta.textContent = '0 chars · 0 B';
        outputMeta.textContent = '0 chars · 0 B';
        inputStatus.className = 'b64-status-tag valid';
        inputStatus.textContent = 'Ready';
        outputStatus.textContent = '+0% overhead';
        if (hexContainer) hexContainer.textContent = '// Enter text to inspect raw hexadecimal byte stream';
        return;
      }

      // Input stats
      const inBytes = new TextEncoder().encode(input).length;
      inputMeta.textContent = `${input.length} chars · ${inBytes} B`;

      if (mode === 'encode') {
        try {
          const rawBytes = stringToBytes(input, charset);
          let b64 = bytesToBase64(rawBytes);

          // Flavor transform
          if (flavor === 'url') {
            b64 = b64.replace(/\+/g, '-').replace(/\//g, '_');
          }

          // Padding handling
          if (!padding) {
            b64 = b64.replace(/=+$/, '');
          }

          // Line wrapping
          if (lineWrap === '64') {
            b64 = b64.match(/.{1,64}/g)?.join('\n') || b64;
          } else if (lineWrap === '76') {
            b64 = b64.match(/.{1,76}/g)?.join('\n') || b64;
          }

          outputArea.value = b64;
          const outBytes = new TextEncoder().encode(b64).length;
          outputMeta.textContent = `${b64.length} chars · ${outBytes} B`;

          const ratio = inBytes > 0 ? (((outBytes - inBytes) / inBytes) * 100).toFixed(1) : '0';
          outputStatus.textContent = `+${ratio}% Base64 overhead`;

          inputStatus.className = 'b64-status-tag valid';
          inputStatus.textContent = `✓ Valid ${charset.toUpperCase()}`;

          // Update Hex view
          updateHexView(rawBytes);
        } catch (err) {
          outputArea.value = `// Encoding Error: ${err.message}`;
          inputStatus.className = 'b64-status-tag invalid';
          inputStatus.textContent = `✕ ${err.message}`;
        }
      } else {
        // Mode === 'decode'
        try {
          // Clean whitespace and linebreaks
          let cleanB64 = input.replace(/\s+/g, '');

          // Normalize Base64URL characters to standard Base64
          cleanB64 = cleanB64.replace(/-/g, '+').replace(/_/g, '/');

          // Add padding if missing
          const padLen = (4 - (cleanB64.length % 4)) % 4;
          cleanB64 += '='.repeat(padLen);

          // Validation check
          const invalidCharMatch = cleanB64.match(/[^A-Za-z0-9+/=]/);
          if (invalidCharMatch) {
            const char = invalidCharMatch[0];
            const idx = input.indexOf(char);
            throw new Error(`Invalid character '${char}' at index ${idx}`);
          }

          const rawBytes = base64ToBytes(cleanB64);
          const decoded = bytesToString(rawBytes, charset);

          outputArea.value = decoded;
          const outBytes = new TextEncoder().encode(decoded).length;
          outputMeta.textContent = `${decoded.length} chars · ${outBytes} B`;

          inputStatus.className = 'b64-status-tag valid';
          inputStatus.textContent = `✓ Valid Base64 (${cleanB64.length} encoded bytes)`;

          const compression = inBytes > 0 ? (((inBytes - outBytes) / inBytes) * 100).toFixed(1) : '0';
          outputStatus.textContent = `-${compression}% decoded size`;

          // Update Hex view
          updateHexView(rawBytes);
        } catch (err) {
          outputArea.value = `// Decoding Error: ${err.message}`;
          inputStatus.className = 'b64-status-tag invalid';
          inputStatus.textContent = `✕ ${err.message}`;
        }
      }
    }

    // Format Hex Bytes into aligned rows
    function updateHexView(bytes) {
      if (!hexContainer) return;
      if (!bytes || bytes.length === 0) {
        hexContainer.textContent = '// No bytes available';
        return;
      }
      let out = '';
      const hexArr = [];
      for (let i = 0; i < bytes.length; i++) {
        hexArr.push(bytes[i].toString(16).padStart(2, '0'));
      }
      // Group in blocks of 16
      for (let i = 0; i < hexArr.length; i += 16) {
        const offset = i.toString(16).padStart(4, '0');
        const chunk = hexArr.slice(i, i + 16).join(' ');
        out += `${offset}:  ${chunk}\n`;
      }
      hexContainer.textContent = out.trim();
    }

    // Switch between Encode and Decode modes
    function setMode(newMode) {
      mode = newMode;
      pillEncode.classList.toggle('active', mode === 'encode');
      pillDecode.classList.toggle('active', mode === 'decode');

      if (mode === 'encode') {
        inputTitle.innerHTML = '📄 Plain Text Input';
        outputTitle.innerHTML = '🔐 Base64 Encoded Output';
        inputArea.placeholder = 'Type or paste plain text here to encode...';
      } else {
        inputTitle.innerHTML = '🔐 Base64 Encoded Input';
        outputTitle.innerHTML = '📄 Decoded Text Output';
        inputArea.placeholder = 'Paste Base64 or Base64URL string here to decode...';
      }
      process();
      window.Analytics?.trackToolAction('base64', 'switch_mode', { mode: newMode });
    }

    // Event Listeners
    inputArea.addEventListener('input', process);

    pillEncode.addEventListener('click', () => setMode('encode'));
    pillDecode.addEventListener('click', () => setMode('decode'));

    pillStandard.addEventListener('click', () => {
      flavor = 'standard';
      pillStandard.classList.add('active');
      pillUrl.classList.remove('active');
      process();
      showToast('Flavor: Standard RFC 4648 (+, /, =)', '🔤');
    });

    pillUrl.addEventListener('click', () => {
      flavor = 'url';
      pillUrl.classList.add('active');
      pillStandard.classList.remove('active');
      process();
      showToast('Flavor: Base64URL RFC 4648 §5 (-, _ for JWTs)', '🌐');
    });

    if (charsetSelect) {
      charsetSelect.addEventListener('change', (e) => {
        charset = e.target.value;
        process();
        showToast(`Charset: ${charset.toUpperCase()}`, '⚙️');
      });
    }

    if (paddingCheck) {
      paddingCheck.addEventListener('change', (e) => {
        padding = e.target.checked;
        process();
      });
    }

    if (lineWrapSelect) {
      lineWrapSelect.addEventListener('change', (e) => {
        lineWrap = e.target.value;
        process();
      });
    }

    // Swap input & output
    btnSwap.addEventListener('click', () => {
      const temp = outputArea.value;
      if (!temp || temp.startsWith('//')) {
        showToast('Nothing to swap', '⚠️');
        return;
      }
      inputArea.value = temp;
      setMode(mode === 'encode' ? 'decode' : 'encode');
      showToast('Swapped Input & Output', '⇄');
    });

    // Copy to clipboard
    btnCopy.addEventListener('click', () => {
      const text = outputArea.value;
      if (!text || text.startsWith('//')) {
        showToast('Output is empty', '⚠️');
        return;
      }
      navigator.clipboard.writeText(text).then(() => {
        showToast('Copied result to clipboard!', '📋');
      }).catch(() => {
        showToast('Failed to copy', '✕');
      });
    });

    // Clear input
    btnClear.addEventListener('click', () => {
      inputArea.value = '';
      outputArea.value = '';
      process();
      inputArea.focus();
      showToast('Cleared input', '🗑️');
      window.Analytics?.trackToolAction('base64', 'clear');
    });

    // Download result text
    btnDownload.addEventListener('click', () => {
      const text = outputArea.value;
      if (!text || text.startsWith('//')) {
        showToast('Output is empty', '⚠️');
        return;
      }
      const filename = mode === 'encode' ? (flavor === 'url' ? 'encoded-base64url.txt' : 'encoded-base64.txt') : 'decoded-text.txt';
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(`Downloaded ${filename}`, '💾');
    });

    // Presets dropdown
    if (presetSelect) {
      presetSelect.addEventListener('change', (e) => {
        const val = e.target.value;
        if (PRESETS[val]) {
          inputArea.value = PRESETS[val];
          if (mode === 'decode') setMode('encode');
          process();
          showToast(`Loaded Preset: ${e.target.options[e.target.selectedIndex].text}`, '📂');
          window.Analytics?.track('preset_select', { tool_name: 'base64', preset_name: val });
        }
      });
    }

    // Toggle Hex Drawer
    if (btnToggleHex && hexDrawer) {
      btnToggleHex.addEventListener('click', () => {
        hexDrawer.hidden = !hexDrawer.hidden;
        btnToggleHex.textContent = hexDrawer.hidden ? '🔍 Inspect Hex Bytes' : '✕ Hide Hex Bytes';
      });
    }

    // Initial load with default "Hello World" preset
    inputArea.value = PRESETS.hello;
    process();
  });

})();
